import { FC, ReactNode, useCallback } from 'react';
import { Outlet, Navigate } from 'react-router-dom';
import { AppProvider, DashboardLayout, Router, NavigationPageItem, DashboardSidebarPageItem } from '@toolpad/core';
import { Box, Container } from '@mui/material';

import { AuthRole } from '@libs/types';
import { theme } from '@carcareers/theme';
import { useAuth } from '@carcareers/hooks';
import { Snackbar, Loading, Logo, ToolbarActions } from '@carcareers/components';
import { routes, navigation } from '@carcareers/utils';
import { useDrawer } from '@carcareers/contexts';

import { RooftopPageItem } from './CustomPageItems';

export interface DashboardShellProps {
  children?: ReactNode;
}

export const DashboardShell: FC<DashboardShellProps> = ({ children }) => {
  const auth = useAuth();
  const { open: drawerOpen, width: drawerWidth } = useDrawer();
  const widthOffset = !drawerOpen || (drawerWidth && drawerWidth > 500) ? 0 : drawerWidth ?? 0;

  const customPageItemRenderer = useCallback((item: NavigationPageItem, { mini }: { mini: boolean }) => {
    switch (item.title) {
      case 'Rooftops':
        return <RooftopPageItem item={item} />;
      default:
        return <DashboardSidebarPageItem item={item} />;
    }
  }, []);

  const role = auth.session.user?.role as AuthRole;
  if (!role) {
    return <Navigate to={routes.HOME} />;
  }

  const nav = navigation[role];
  const branding = {
    logo: <Logo role={role} sx={{ display: 'flex', width: 200, my: '3px' }} />,
    title: '',
  };

  const router: Router = {
    pathname: window.location.pathname,
    searchParams: new URLSearchParams(window.location.search),
    navigate: (url) => {
      window.location.href = url.toString();
    },
  };

  return (
    <AppProvider branding={branding} router={router} navigation={nav} theme={theme}>
      <DashboardLayout
        slots={{ toolbarActions: ToolbarActions }}
        renderPageItem={customPageItemRenderer}
        sx={{ backgroundColor: 'grey.100' }}
      >
        <Snackbar />
        <Loading />
        <Box
          sx={{
            width: `calc(100% - ${widthOffset}px)`,
            transition: 'width 225ms cubic-bezier(0, 0, 0.2, 1)',
          }}
        >
          <Container maxWidth="lg">
            <Outlet />
            {children}
          </Container>
        </Box>
      </DashboardLayout>
    </AppProvider>
  );
};

export default DashboardShell;
